<template>
  <div class="org-unassigned pa-4">
    <v-btn text small class="mb-2 px-1" :to="{ name: 'ClientOrgCharts' }">
      <v-icon small left>mdi-arrow-left</v-icon>
      Organigramas
    </v-btn>

    <h2 class="org-unassigned__title mb-1">Contactos sin empresa ({{ total }})</h2>
    <p class="org-unassigned__hint mb-4">Elige en qué empresa trabaja cada contacto y confírmalo, o descártalo.</p>

    <v-card>
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Buscar contacto"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          hide-details
          class="org-unassigned__search"
        />
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="candidates"
        :loading="loadingCandidates"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
        no-data-text="No hay contactos sin empresa"
        loading-text="Cargando contactos..."
      >
        <template #[`item.fullName`]="{ item }">
          {{ item.fullName }}
          <v-btn
            v-if="item.linkedinUrl"
            icon
            x-small
            :href="item.linkedinUrl"
            target="_blank"
            rel="noopener"
            aria-label="Ver LinkedIn"
          >
            <v-icon x-small>mdi-linkedin</v-icon>
          </v-btn>
        </template>
        <template #[`item.company`]="{ item }">
          <v-select
            :value="selectedCompanies[item.id]"
            :items="companyItems(item)"
            placeholder="Elige la empresa"
            dense
            hide-details
            class="org-unassigned__company"
            @input="selectCompany(item.id, $event)"
          />
        </template>
        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-end">
            <v-btn
              small
              depressed
              color="primary"
              class="mr-1"
              :disabled="!selectedCompanies[item.id]"
              @click="openConfirm(item)"
            >
              Confirmar
            </v-btn>
            <v-btn small text color="error" @click="askDiscard(item)">Descartar</v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <confirm-candidate-modal
      v-model="confirmOpen"
      :candidate="confirmingCandidate"
      :company="confirmingCompany"
      @confirmed="onConfirmed"
      @changed="loadCandidates"
    />

    <v-dialog v-model="discardOpen" max-width="400px">
      <v-card v-if="discardingCandidate">
        <v-card-title>¿Descartar contacto?</v-card-title>
        <v-card-text>{{ discardingCandidate.fullName }} sale de la lista y no se agrega a Pipedrive.</v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer />
          <v-btn text :disabled="discarding" @click="discardOpen = false">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="discarding" @click="discard">Descartar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ClientOrgChartsService from '../services/client-org-charts.service';
import ConfirmCandidateModal from '../components/ConfirmCandidateModal.vue';
import { utilsMixin } from '../mixins/utilsMixin';
import { errorMessage } from '../utils/org-chart.utils';

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: 'ClientOrgChartUnassigned',
  components: { ConfirmCandidateModal },
  mixins: [utilsMixin],
  data() {
    return {
      search: '',
      options: { page: 1, itemsPerPage: 25 },
      candidates: [],
      total: 0,
      loadingCandidates: false,
      // candidateId -> companyId elegido
      selectedCompanies: {},
      confirmOpen: false,
      confirmingCandidate: null,
      confirmingCompany: null,
      discardOpen: false,
      discardingCandidate: null,
      discarding: false,
      searchTimer: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      headers: [
        { text: 'Nombre', value: 'fullName', sortable: false },
        { text: 'Cargo', value: 'title', sortable: false },
        { text: 'Encontrado por', value: 'foundBy', sortable: false },
        { text: 'Empresa', value: 'company', sortable: false, width: 260 },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ]
    };
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.resetPageAndLoad, SEARCH_DEBOUNCE_MS);
    },
    options: {
      deep: true,
      handler(next, prev) {
        if (!prev || next.page !== prev.page || next.itemsPerPage !== prev.itemsPerPage) this.loadCandidates();
      }
    }
  },
  created() {
    this.loadCandidates();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    resetPageAndLoad() {
      if (this.options.page !== 1) this.options = { ...this.options, page: 1 };
      else this.loadCandidates();
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      const res = await ClientOrgChartsService.getUnassignedCandidates({
        search: this.search ? this.search.trim() : '',
        page: this.options.page,
        pageSize: this.options.itemsPerPage
      });
      this.loadingCandidates = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos cargar los contactos.'));
        return;
      }
      this.candidates = res.data || [];
      this.total = (res.meta && res.meta.total) || this.candidates.length;
      this.preselectSingleCompany();
    },
    preselectSingleCompany() {
      this.candidates.forEach(candidate => {
        const companies = candidate.possibleCompanies || [];
        if (companies.length === 1 && !this.selectedCompanies[candidate.id]) {
          this.$set(this.selectedCompanies, candidate.id, companies[0].companyId);
        }
      });
    },
    companyItems(candidate) {
      return (candidate.possibleCompanies || []).map(company => ({
        text: company.country ? `${company.name} (${company.country})` : company.name,
        value: company.companyId
      }));
    },
    selectCompany(candidateId, companyId) {
      this.$set(this.selectedCompanies, candidateId, companyId);
    },
    openConfirm(candidate) {
      const companyId = this.selectedCompanies[candidate.id];
      this.confirmingCompany = (candidate.possibleCompanies || []).find(company => company.companyId === companyId) || null;
      this.confirmingCandidate = candidate;
      this.confirmOpen = true;
    },
    onConfirmed() {
      this.showSuccessSnackbar('Guardado en Pipedrive');
      this.loadCandidates();
    },
    askDiscard(candidate) {
      this.discardingCandidate = candidate;
      this.discardOpen = true;
    },
    async discard() {
      this.discarding = true;
      const res = await ClientOrgChartsService.discardCandidate(this.discardingCandidate.id);
      this.discarding = false;
      if (res.status !== 'ok') {
        this.showErrorSnackbar(errorMessage(res, 'No pudimos descartarlo. Intenta de nuevo.'));
        return;
      }
      this.discardOpen = false;
      this.showSuccessSnackbar('Contacto descartado');
      this.loadCandidates();
    }
  }
};
</script>

<style scoped>
.org-unassigned__title {
  font-size: 22px;
  font-weight: 600;
  color: var(--pw-text);
}

.org-unassigned__hint {
  color: var(--pw-text-muted);
}

.org-unassigned__search {
  max-width: 420px;
}

.org-unassigned__company {
  min-width: 200px;
}
</style>
